"use client";

import React from 'react';
import { Select, ComboboxItem } from '@mantine/core';
import { cn } from '@/lib/utils';

interface MantineSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  // Props for modal context (automatically handled by Mantine)
  highZIndex?: boolean; // Deprecated - Mantine handles this automatically
}

/**
 * Mantine Select Component
 * 
 * A comprehensive select component that uses Mantine's Select with automatic z-index management.
 * This eliminates the need for manual z-index management in modal contexts.
 * 
 * Key Benefits:
 * - Automatic z-index management (no more highZIndex prop needed)
 * - Better accessibility and focus management
 * - Consistent styling with your design system
 * - Built-in search and clear functionality
 * 
 * Usage:
 * ```tsx
 * <MantineSelect
 *   value={selectedValue}
 *   onValueChange={setSelectedValue}
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 *   placeholder="Select an option..."
 *   searchable
 *   clearable
 * />
 * ```
 */
export const MantineSelect = React.memo(function MantineSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select an option...",
  disabled = false,
  searchable = false,
  clearable = false,
  size = 'md',
  radius = 'md',
  className,
  highZIndex, // Deprecated but kept for backward compatibility
}: MantineSelectProps) {
  // Convert options to Mantine format
  const mantineOptions = React.useMemo(() => 
    options.map(option => ({
      value: option.value,
      label: option.label,
      disabled: option.disabled,
    })), [options]
  );

  return (
    <Select
      value={value}
      onChange={(value: string | null) => onValueChange(value || '')}
      data={mantineOptions}
      placeholder={placeholder}
      disabled={disabled}
      searchable={searchable}
      clearable={clearable}
      size={size}
      radius={radius}
      className={cn(className)}
      // Mantine automatically handles z-index management
      // No need for manual z-index props
    />
  );
});

/**
 * Mantine Multi Select Component
 * 
 * A multi-select component using Mantine's MultiSelect with automatic z-index management.
 * 
 * Usage:
 * ```tsx
 * <MantineMultiSelect
 *   value={selectedValues}
 *   onValueChange={setSelectedValues}
 *   options={options}
 *   placeholder="Select multiple options..."
 *   searchable
 *   clearable
 * />
 * ```
 */
interface MantineMultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  maxValues?: number;
}

export const MantineMultiSelect = React.memo(function MantineMultiSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select multiple options...",
  disabled = false,
  searchable = false,
  clearable = false,
  size = 'md',
  radius = 'md',
  className,
  maxValues,
}: MantineMultiSelectProps) {
  // Convert options to Mantine format
  const mantineOptions = React.useMemo(() => 
    options.map(option => ({
      value: option.value,
      label: option.label,
      disabled: option.disabled,
    })), [options]
  );

  return (
    <Select
      value={value[0] || ''}
      onChange={(value: string | null) => onValueChange(value ? [value] : [])}
      data={mantineOptions}
      placeholder={placeholder}
      disabled={disabled}
      searchable={searchable}
      clearable={clearable}
      size={size}
      radius={radius}
      className={cn(className)}
      // Mantine automatically handles z-index management
    />
  );
});
